import React, { useState, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHistory, faEnvelope, faEye, faFileContract, faRectangleList, faClipboardList, faSpinner, faRepeat, faBell } from '@fortawesome/free-solid-svg-icons';
import ActivityModal from './ActivityModal';
import CreateMessageModal from './CreateMessageModal';
import ViewMessagesModal from './ViewMessagesModal';
import BaseMessageModal, { ModalMode } from './BaseMessageModal';
import { useNavigate } from 'react-router-dom';
import CommTemplateManagerModal from './CommTemplateManagerModal';
import { StatusEnum } from '../../enums/statusEnum';
import { DocumentService, sharedApiClient } from '../../fetcher';
import { TaskTypeEnum } from '../../enums/taskTypeEnum';
import {
  useAuth,
  UserAbility,
  useNotifications,
  PATIENT_DIARY_LAYOUT_SIDEBAR,
  PATIENT_DIARY_LAYOUT_TAB,
  DOCUMENT_TAB_VIEW_SINGLE,
  DOCUMENT_TAB_VIEW_CODES,
  DOCUMENT_TAB_VIEW_ALL,
} from '@medprompt/shared';

const PATIENT_NOTE_TYPE = TaskTypeEnum.PATIENT_NOTE.value;

const LayoutChoice = ({ label, value, options, onChange }) => {
  const labelId = `layout-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`;
  return (
  <div className="layout-setting">
    <span className="layout-setting-label" id={labelId}>{label}</span>
    <div className="layout-choice-list" role="radiogroup" aria-labelledby={labelId}>
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            className={`layout-choice${selected ? ' is-selected' : ''}`}
            onClick={() => {
              if (!selected) {
                onChange(option.value);
              }
            }}
          >
            <span className="layout-choice-title">{option.label}</span>
            <span className="layout-choice-detail">{option.detail}</span>
          </button>
        );
      })}
    </div>
  </div>
  );
};

const ActionsTab = ({
  doc,
  activeUsers,
  teams = [],
  patientData,
  onDocumentUpdate,
  patientDiaryLayout,
  onPatientDiaryLayoutChange,
  tabViewMode,
  onTabViewModeChange,
}) => {
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [isCreateMessageModalOpen, setIsCreateMessageModalOpen] = useState(false);
  const [isViewMessagesModalOpen, setIsViewMessagesModalOpen] = useState(false);
  const [isContractViolationModalOpen, setIsContractViolationModalOpen] = useState(false);
  const navigate = useNavigate();
  const [isTemplateManagerModalOpen, setIsTemplateManagerModalOpen] = useState(false);
  const [templateRefreshTrigger, setTemplateRefreshTrigger] = useState(0);
  const [autoOpenGenerateConfirmation, setAutoOpenGenerateConfirmation] = useState(false);
  const [isWritePatientPlanLoading, setIsWritePatientPlanLoading] = useState(false);
  const [isRegenerateLoading, setIsRegenerateLoading] = useState(false);
  const documentManager = useMemo(() => new DocumentService(sharedApiClient), []);
  const { addNotification } = useNotifications();
  const { user } = useAuth();
  const hasDocsWrite = Boolean(user?.authorities?.includes(UserAbility.DOCS_WRITE.key));
  const canWriteDocument = hasDocsWrite && doc.status === StatusEnum.READY.value;

  const handleOpenActivity = () => {
    setIsActivityModalOpen(true);
  };

  const handleCloseActivity = () => {
    setIsActivityModalOpen(false);
  };

  const handleOpenCreateMessage = () => {
    setAutoOpenGenerateConfirmation(false);
    setIsCreateMessageModalOpen(true);
  };

  const handleCloseCreateMessage = () => {
    setIsCreateMessageModalOpen(false);
    setAutoOpenGenerateConfirmation(false);
  };

  const handleRegenerate = useCallback(async () => {
    if (!canWriteDocument) {
      return;
    }
    if (!doc?.documentId) {
      return;
    }
    setIsRegenerateLoading(true);
    try {
      await documentManager.regenerateDocument(doc.documentId);
      addNotification(`regenerate-success-${doc.documentId}`, 'Document queued for regeneration');
    } catch (error) {
      console.error('Error regenerating document:', error);
      addNotification(`regenerate-error-${doc.documentId}`, 'Failed to regenerate document', { isError: true });
    } finally {
      setIsRegenerateLoading(false);
    }
  }, [canWriteDocument, doc?.documentId, documentManager, addNotification]);

  const handleWritePatientPlan = useCallback(async () => {
    if (!canWriteDocument) {
      return;
    }
    if (!onDocumentUpdate || !doc?.documentId || !doc.outcomeTasks?.length) {
      return;
    }
    const patientNoteTasks = doc.outcomeTasks.filter(t => t.taskType === PATIENT_NOTE_TYPE);
    if (patientNoteTasks.length === 0) {
      return;
    }

    setIsWritePatientPlanLoading(true);
    try {
      const toPersist = patientNoteTasks.filter(t => !t.isSelected);
      await Promise.all(
        toPersist.map(task =>
          documentManager.updateDocumentTask(doc.documentId, task.taskId, {
            isSelected: true,
            isDeselected: null,
          })
        )
      );

      const updatedTasks = doc.outcomeTasks.map(task =>
        task.taskType === PATIENT_NOTE_TYPE ? { ...task, isSelected: true } : task
      );
      onDocumentUpdate({ ...doc, outcomeTasks: updatedTasks });
      setAutoOpenGenerateConfirmation(true);
      setIsCreateMessageModalOpen(true);
    } catch (error) {
      console.error('Failed to select patient message tasks:', error);
      addNotification('write-patient-plan-error', 'Failed to update outcome tasks', { isError: true });
    } finally {
      setIsWritePatientPlanLoading(false);
    }
  }, [canWriteDocument, doc, documentManager, onDocumentUpdate, addNotification]);

  const handleOpenViewMessages = () => {
    setIsViewMessagesModalOpen(true);
  };

  const handleCloseViewMessages = () => {
    setIsViewMessagesModalOpen(false);
  };

  const handleOpenContractViolation = () => {
    setIsContractViolationModalOpen(true);
  };

  const handleCloseContractViolation = () => {
    setIsContractViolationModalOpen(false);
  };

  const handleOpenTemplateManager = () => {
    setIsTemplateManagerModalOpen(true);
  };

  const handleCloseTemplateManager = () => {
    setIsTemplateManagerModalOpen(false);
    // Increment trigger to force CreateMessageModal to refetch templates
    setTemplateRefreshTrigger(prev => prev + 1);
  };

  const handleMessageSent = () => {
    // Optionally refresh messages or show notification
    console.log('Message sent successfully');
  };

  // Check if patient demographics are available for sending messages
  const canSendMessage = !!patientData;
  const docsWriteTitle = 'You do not have permission to edit documents';
  
  // Check if contract violations are available
  const hasContractViolations = doc?.lmcContractViolations && doc.lmcContractViolations.length > 0;

  const hasPatientNoteTasks = doc?.outcomeTasks?.some(t => t.taskType === PATIENT_NOTE_TYPE);

  return (
    <div className="details-content">
      <div className="detail-tile">
        <h3 className="tile-header">Document</h3>
        <div className="tile-content">
          <button 
            className="button activity-button" 
            onClick={handleOpenActivity}
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              height: '41px',
              fontSize: '16px',
              fontWeight: '500',
              marginBottom: '10px'
            }}
          >
            <span>Open audit trail</span>
            <FontAwesomeIcon icon={faHistory} style={{ fontSize: '18px' }} />
          </button>
          <button 
            className="button activity-button" 
            onClick={handleOpenContractViolation}
            disabled={!canWriteDocument}
            title={
              !hasDocsWrite
                ? docsWriteTitle
                : !hasContractViolations
                  ? 'No contract violations found'
                  : 'Generate transfer of care letter to sender'
            }
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              height: '41px',
              fontSize: '16px',
              fontWeight: '500',
              marginBottom: '10px'
            }}
          >
            <span>Write transfer of care violation letter</span>
            <FontAwesomeIcon icon={faFileContract} style={{ fontSize: '18px' }} />
          </button>
          <button
            className="button activity-button"
            onClick={() => navigate(`/document-detail/${doc.documentId}/quality-alert`)}
            disabled={!canWriteDocument || !patientData}
            title={
              !hasDocsWrite
                ? docsWriteTitle
                : !patientData
                  ? 'A patient is required'
                  : 'Raise a quality alert'
            }
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              height: '41px',
              fontSize: '16px',
              fontWeight: '500',
              marginBottom: '10px'
            }}
          >
            <span>Raise quality alert</span>
            <FontAwesomeIcon icon={faBell} style={{ fontSize: '18px' }} />
          </button>
          <button
            className="button activity-button"
            onClick={handleRegenerate}
            disabled={!canWriteDocument || isRegenerateLoading}
            title={
              !hasDocsWrite
                ? docsWriteTitle
                : doc.status === StatusEnum.FILED_AWAY.value
                  ? 'Filed Away - Regeneration Disabled'
                  : 'Regenerate Document'
            }
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              height: '41px',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            <span>Regenerate document</span>
            {isRegenerateLoading ? (
              <FontAwesomeIcon icon={faSpinner} className="fa-spin" style={{ fontSize: '18px' }} />
            ) : (
              <FontAwesomeIcon icon={faRepeat} style={{ fontSize: '18px' }} />
            )}
          </button>
        </div>
      </div>

      <div className="detail-tile">
        <h3 className="tile-header">Patient Communication</h3>
        <div className="tile-content">
          <button 
            className="button activity-button" 
            onClick={handleOpenViewMessages}
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              height: '41px',
              fontSize: '16px',
              fontWeight: '500',
              marginBottom: '10px'
            }}
          >
            <span>View sent messages</span>
            <FontAwesomeIcon icon={faEye} style={{ fontSize: '18px' }} />
          </button>
          <button 
            className="button activity-button" 
            onClick={handleOpenCreateMessage}
            disabled={!canSendMessage || !canWriteDocument}
            title={
              !hasDocsWrite
                ? docsWriteTitle
                : !canSendMessage
                  ? 'Patient record not available'
                  : 'Send message to patient'
            }
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              height: '41px',
              fontSize: '16px',
              fontWeight: '500',
              marginBottom: '10px'
            }}
          >
            <span>Send message</span>
            <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: '18px' }} />
          </button>
          {hasPatientNoteTasks && (
            <button
              className="button activity-button"
              onClick={handleWritePatientPlan}
              disabled={
                !canSendMessage ||
                !canWriteDocument ||
                isWritePatientPlanLoading ||
                !onDocumentUpdate
              }
              title={
                !hasDocsWrite
                  ? docsWriteTitle
                  : !onDocumentUpdate
                    ? 'Unable to update tasks'
                    : !canSendMessage
                      ? 'Patient record not available'
                      : 'Select all patient message tasks and start generating a message'
              }
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: '41px',
                fontSize: '16px',
                fontWeight: '500',
                marginBottom: '10px',
              }}
            >
              <span>Write patient plan</span>
              {isWritePatientPlanLoading ? (
                <FontAwesomeIcon icon={faSpinner} className="fa-spin" style={{ fontSize: '18px' }} />
              ) : (
                <FontAwesomeIcon icon={faClipboardList} style={{ fontSize: '18px' }} />
              )}
            </button>
          )}
          <button 
            className="button activity-button" 
            onClick={handleOpenTemplateManager}
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              height: '41px',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            <span>Manage message templates</span>
            <FontAwesomeIcon icon={faRectangleList} style={{ fontSize: '18px' }} />
          </button>
        </div>
      </div>

      <div className="detail-tile layout-settings-tile">
        <h3 className="tile-header">Layout</h3>
        <div className="tile-content">
          <LayoutChoice
            label="Patient diary"
            value={patientDiaryLayout}
            onChange={onPatientDiaryLayoutChange}
            options={[
              {
                value: PATIENT_DIARY_LAYOUT_TAB,
                label: 'In a tab',
                detail: 'Opens with the other tabs, and hides when you leave it.',
              },
              {
                value: PATIENT_DIARY_LAYOUT_SIDEBAR,
                label: 'Beside the document',
                detail: 'Stays open in its own column while you work.',
              },
            ]}
          />
          <LayoutChoice
            label="How the tabs open"
            value={tabViewMode}
            onChange={onTabViewModeChange}
            options={[
              {
                value: DOCUMENT_TAB_VIEW_SINGLE,
                label: 'One at a time',
                detail: 'The tab you pick fills this panel.',
              },
              {
                value: DOCUMENT_TAB_VIEW_CODES,
                label: 'Codes side by side',
                detail: 'Existing and new codes open together. Every other tab stays on its own.',
              },
              {
                value: DOCUMENT_TAB_VIEW_ALL,
                label: 'Clinical tabs together',
                detail: 'Details, both code lists, measurements, and outcome tasks share the width. Actions stays a separate tab.',
              },
            ]}
          />
        </div>
      </div>

      {isActivityModalOpen && (
        <ActivityModal
          doc={doc}
          documentId={doc.documentId}
          activeUsers={activeUsers}
          teams={teams}
          onClose={handleCloseActivity}
        />
      )}

      {isCreateMessageModalOpen && (
        <CreateMessageModal
          documentId={doc.documentId}
          patientData={patientData}
          doc={doc}
          onClose={handleCloseCreateMessage}
          onMessageSent={handleMessageSent}
          templateRefreshTrigger={templateRefreshTrigger}
          autoOpenGenerateConfirmation={autoOpenGenerateConfirmation}
        />
      )}

      {isViewMessagesModalOpen && (
        <ViewMessagesModal
          documentId={doc.documentId}
          entityStatus={doc.status}
          onClose={handleCloseViewMessages}
        />
      )}

      {isContractViolationModalOpen && (
        <BaseMessageModal
          mode={ModalMode.CONTRACT_VIOLATION}
          documentId={doc.documentId}
          patientData={patientData}
          doc={doc}
          onClose={handleCloseContractViolation}
        />
      )}

      {isTemplateManagerModalOpen && (
        <CommTemplateManagerModal
          onClose={handleCloseTemplateManager}
        />
      )}
    </div>
  );
};

export default ActionsTab; 